// Live snapshots over Server-Sent Events. The browser's EventSource resends Last-Event-ID on reconnect.
import { useSignal } from '@preact/signals';
import { useEffect } from 'preact/hooks';
import { api } from './lib.ts';

export function useLive<T extends { v: number }>(url: string) {
  const snap = useSignal<T | null>(null);
  const error = useSignal<string | null>(null);
  const generation = useSignal(0);
  const accept = (s: T) => {
    const cur = snap.peek();
    if (!cur || s.v >= cur.v || (s as unknown as { id: string }).id !== (cur as unknown as { id: string }).id)
      snap.value = s;
  };
  useEffect(() => {
    const es = new EventSource(url);
    es.addEventListener('snapshot', (e) => accept(JSON.parse((e as MessageEvent).data)));
    es.addEventListener('gone', () => {
      error.value = 'This no longer exists.';
      es.close();
    });
    es.onerror = async () => {
      if (es.readyState !== EventSource.CLOSED) return; // the browser retries by itself
      try {
        await api(url.replace(/\/events$/, ''));
        setTimeout(() => generation.value++, 2000);
      } catch (e) {
        error.value = e instanceof Error ? e.message : 'Not found';
      }
    };
    return () => es.close();
  }, [url, generation.value]);
  return {
    snap,
    error,
    /** Take a fresher snapshot (e.g. a mutation's response). */
    accept,
    /** Reconnect, e.g. after joining (new cookie = new viewer). */
    reconnect: () => {
      generation.value++;
    },
  };
}
